import { call, put, takeLatest } from "redux-saga/effects";
import { axiosInstance } from "../../utils/AxiosInstance";
import {
  getAllInspectionFirmsFailure,
  getAllInspectionFirmsRequest,
  getAllInspectionFirmsSuccess,
  getInspectionFirmDetailsSuccess,
  getInspectionFirmDetailsFailure,
  getOwnershipTypesSuccess,
  getOwnershipTypesFailure,
  getMembershipTypesSuccess,
  getMembershipTypesFailure,
  getInspectionFirmAllUsersFailure,
  getInspectionFirmAllUsersSuccess,
  getTaxDetailsSuccess,
  getTaxDetailsFailure,
  getTaxClassificationsFailure,
  getTaxClassificationsSuccess,
  getInsuranceTypesSuccess,
  getInsuranceTypesFailure,
  getInsurancesFailure,
  getInsurancesSuccess,
  getServicesSuccess,
  getServicesFailure,
  getInspectionServicesSuccess,
  getInspectionServicesFailure,
  getCertificationTypesSuccess,
  getCertificationTypesFailure,
  getCoverageAreasFailure,
  getCoverageAreasSuccess,
  getInspectionFirmUsersSuccess,
  getInspectionFirmUsersFailure,
  getInspectionFirmDocumentsFailure,
  getInspectionFirmDocumentsSuccess,
  getCertificationMethodsFailure,
  getCertificationMethodsSuccess,
  getCurrentInspectionFirmDetailsSuccess,
  getCurrentInspectionFirmDetailsFailure,
  getCurrentTaxDetailsSuccess,
  getCurrentTaxDetailsFailure,
  getCurrentInsurancesSuccess,
  getCurrentInsurancesFailure,
  getCurrentServicesSuccess,
  getCurrentServicesFailure,
  getCurrentCoverageAreasSuccess,
  getCurrentCoverageAreasFailure,
} from "./slice";
import { AxiosError, AxiosResponse } from "axios";
import { INSPECTION_FIRMS_REQUEST } from "./types";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function* fetchInspectionFirms(
  action: ReturnType<typeof getAllInspectionFirmsRequest>
): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors`,
      {
        params: action.payload,
      }
    );
    if (response?.status === 200) {
      yield put(getAllInspectionFirmsSuccess(response.data));
    } else {
      yield put(
        getAllInspectionFirmsFailure(
          response?.data?.message || "Failed to fetch inspection firms"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getAllInspectionFirmsFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* getInspectionFirmDetails(action: any) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/inspectors/${id}`
    );
    yield put(getInspectionFirmDetailsSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getInspectionFirmDetailsFailure({ error: err.response?.data }));
  }
}

function* fetchOwnershipTypes(): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/ownership-types`
    );
    if (response?.status === 200) {
      yield put(getOwnershipTypesSuccess(response.data));
    } else {
      yield put(
        getOwnershipTypesFailure(
          response?.data?.message || "Failed to fetch ownership types"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getOwnershipTypesFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* fetchMembershipTypes(): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/membership-types`
    );
    if (response?.status === 200) {
      yield put(getMembershipTypesSuccess(response.data));
    } else {
      yield put(
        getMembershipTypesFailure(
          response?.data?.message || "Failed to fetch membership types"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getMembershipTypesFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* getInspectionFirmAllUsers(action: any) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/users/inspectors/${id}/list`
    );
    yield put(getInspectionFirmAllUsersSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getInspectionFirmAllUsersFailure({ error: err.response?.data }));
  }
}

function* getTaxDetails(action: any) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/inspectors/${id}/tax-details`
    );
    yield put(getTaxDetailsSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getTaxDetailsFailure({ error: err.response?.data }));
  }
}

function* fetchTaxClassifications(): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/tax-classifications`
    );
    if (response?.status === 200) {
      yield put(getTaxClassificationsSuccess(response.data));
    } else {
      yield put(
        getTaxClassificationsFailure(
          response?.data?.message || "Failed to fetch tax classifications"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getTaxClassificationsFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* fetchInsuranceTypes(): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/insurance-types`
    );
    if (response?.status === 200) {
      yield put(getInsuranceTypesSuccess(response.data));
    } else {
      yield put(
        getInsuranceTypesFailure(
          response?.data?.message || "Failed to fetch membership types"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getInsuranceTypesFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* fetchInsurances(action: any) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/${id}/insurances`
    );
    if (response?.status === 200) {
      yield put(getInsurancesSuccess(response.data));
    } else {
      yield put(
        getInsurancesFailure(
          response?.data?.message || "Failed to fetch insurances"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getInsurancesFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* fetchServices(action: any) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/${id}/services`
    );
    if (response?.status === 200) {
      yield put(getServicesSuccess(response.data));
    } else {
      yield put(
        getServicesFailure(
          response?.data?.message || "Failed to fetch services"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getServicesFailure(error?.response?.data?.message || "An error occurred")
    );
  }
}

function* fetchInspectionServices(): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/inspection-services`
    );
    if (response?.status === 200) {
      yield put(getInspectionServicesSuccess(response.data));
    } else {
      yield put(
        getInspectionServicesFailure(
          response?.data?.message || "Failed to fetch services"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getInspectionServicesFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* fetchCertificationTypes(): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/certification-types`
    );
    if (response?.status === 200) {
      yield put(getCertificationTypesSuccess(response.data));
    } else {
      yield put(
        getCertificationTypesFailure(
          response?.data?.message || "Failed to fetch services"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getCertificationTypesFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* fetchCoverageAreas(action: any) {
  const { id } = action.payload;
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/${id}/coverage-areas`
    );
    if (response?.status === 200) {
      yield put(getCoverageAreasSuccess(response.data));
    } else {
      yield put(
        getCoverageAreasFailure(
          response?.data?.message || "Failed to fetch coverage areas"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getCoverageAreasFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* getInspectionFirmUsers(action: any) {
  const { id, ...params } = action.payload;
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/inspectors/${id}/users`,
      {
        params,
      }
    );
    yield put(getInspectionFirmUsersSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getInspectionFirmUsersFailure({ error: err.response?.data }));
  }
}

function* getInspectionFirmDocuments(action: any) {
  const { id, ...params } = action.payload;
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/inspectors/${id}/documents`,
      {
        params,
      }
    );
    yield put(getInspectionFirmDocumentsSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getInspectionFirmDocumentsFailure({ error: err.response?.data }));
  }
}

function* fetchCertificationMethods(): Generator<any, void, any> {
  try {
    const response: AxiosResponse<any> = yield call(
      axiosInstance.get,
      `${API_BASE_URL}/api/v1/inspectors/certification-methods`
    );
    if (response?.status === 200) {
      yield put(getCertificationMethodsSuccess(response.data));
    } else {
      yield put(
        getCertificationMethodsFailure(
          response?.data?.message || "Failed to fetch services"
        )
      );
    }
  } catch (error: any) {
    yield put(
      getCertificationMethodsFailure(
        error?.response?.data?.message || "An error occurred"
      )
    );
  }
}

function* getCurrentInspectionFirmDetails() {
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/inspectors/current`
    );
    yield put(
      getCurrentInspectionFirmDetailsSuccess({ response: response?.data })
    );
  } catch (error) {
    const err = error as AxiosError;
    yield put(
      getCurrentInspectionFirmDetailsFailure({ error: err.response?.data })
    );
  }
}

function* getCurrentTaxDetails() {
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/inspectors/current/tax-details`
    );
    yield put(getCurrentTaxDetailsSuccess({ response: response?.data }));
  } catch (error) {
    const err = error as AxiosError;
    yield put(getCurrentTaxDetailsFailure({ error: err.response?.data }));
  }
}

function* getCurrentInsurances() {
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/inspectors/current/insurances`
    );
    yield put(getCurrentInsurancesSuccess(response.data));
  } catch (error: any) {
    const err = error as AxiosError;
    yield put(getCurrentInsurancesFailure({ error: err.response?.data }));
  }
}

function* getCurrentServices() {
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/inspectors/current/services`
    );
    yield put(getCurrentServicesSuccess(response.data));
  } catch (error: any) {
    const err = error as AxiosError;
    yield put(getCurrentServicesFailure({ error: err.response?.data }));
  }
}

function* getCurrentCoverageAreas() {
  try {
    const response: AxiosResponse<any> = yield axiosInstance.get(
      `${API_BASE_URL}/api/v1/inspectors/current/coverage-areas`
    );
    yield put(getCurrentCoverageAreasSuccess(response.data));
  } catch (error: any) {
    const err = error as AxiosError;
    yield put(getCurrentCoverageAreasFailure({ error: err.response?.data }));
  }
}

export function* inspectionFirmsSaga(): Generator<any, void, any> {
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getAllInspectionFirmsRequest`,
    fetchInspectionFirms
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getInspectionFirmDetailsRequest`,
    getInspectionFirmDetails
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getOwnershipTypesRequest`,
    fetchOwnershipTypes
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getMembershipTypesRequest`,
    fetchMembershipTypes
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getInspectionFirmAllUsersRequest`,
    getInspectionFirmAllUsers
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getTaxDetailsRequest`,
    getTaxDetails
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getTaxClassificationsRequest`,
    fetchTaxClassifications
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getInsuranceTypesRequest`,
    fetchInsuranceTypes
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getInsurancesRequest`,
    fetchInsurances
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getServicesRequest`,
    fetchServices
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getInspectionServicesRequest`,
    fetchInspectionServices
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getCertificationTypesRequest`,
    fetchCertificationTypes
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getCoverageAreasRequest`,
    fetchCoverageAreas
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getInspectionFirmUsersRequest`,
    getInspectionFirmUsers
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getInspectionFirmDocumentsRequest`,
    getInspectionFirmDocuments
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getCertificationMethodsRequest`,
    fetchCertificationMethods
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getCurrentInspectionFirmDetailsRequest`,
    getCurrentInspectionFirmDetails
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getCurrentTaxDetailsRequest`,
    getCurrentTaxDetails
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getCurrentInsurancesRequest`,
    getCurrentInsurances
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getCurrentServicesRequest`,
    getCurrentServices
  );
  yield takeLatest(
    `${INSPECTION_FIRMS_REQUEST}/getCurrentCoverageAreasRequest`,
    getCurrentCoverageAreas
  );
}
